/**
 * @file This library is for billing bank services components that are fetched by BankServicesQuery.
 */

import { addMonths, endOfDay, format, startOfDay, subMonths } from "date-fns";
import {
  Service,
  ServiceStatus,
  ServiceType,
  Plan,
  PlanStatus,
} from "@/generated/graphql_billing";
import { ServiceNames } from "@/libs/constants";

/**
 * Filter Mikatano services.
 */
export const filterMikatanoServices = (services: Service[]) => {
  return services.filter((service) => service.type === ServiceType.Mikatano);
};

/**
 * Filter active Mikatano services except for the Mikatano LoanPortal.
 * @returns Filtered active Mikatano services except for the Mikatano LoanPortal.
 */
export const filterActiveMikatanoServicesExceptLoan = (services: Service[]) => {
  return services.filter(
    (service) =>
      service.status === ServiceStatus.Active &&
      service.type === ServiceType.Mikatano &&
      service.code !== ServiceNames.Loan.CODE
  );
};

/**
 * Filter active Other services.
 * @returns Filtered active Other services.
 */
export const filterActiveOtherServices = (services: Service[]) => {
  return services.filter(
    (service) =>
      service.status === ServiceStatus.Active &&
      service.type === ServiceType.Other
  );
};

// shortenServiceName returns a shortened service name. Some service names are too long to display, so they need to be shortened.
/**
 * shortenServiceName returns a shortened service name.
 * @description Some service names are too long to display, so they need to be shortened.
 * @param name - service name
 * @param code - service code
 * @returns shortened service name
 */
export const shortenServiceName = (name: string, code: string): string => {
  switch (code) {
    case ServiceNames.MfcInvoice.CODE:
      const wordToRemove = "プロダクトキー付き※";
      return name.replace(wordToRemove, "");
    default:
      return name;
  }
};

/**
 * Sort services by displayOrder in ascending order.
 * @param {Service[]} services - services to be sorted
 * @returns {Service[]} - sorted services
 */
export const sortServicesByDisplayOrder = (services: Service[]) => {
  // Cautions: The displayOrder might be undefined. (because strict=false at tsconfig.json)
  return services.sort(
    (before, after) => (before.displayOrder || 0) - (after.displayOrder || 0)
  );
};

export type AnnoByServiceCode = {
  code: string;
  annotation: string;
};

export type ServiceAnnotationConfig = {
  order: number;
  annotation: string;
};

/**
 * Create a map of code, annotation and order.
 * @param {AnnoByServiceCode[]} annoByServiceCodes - Annotation By Service Code array
 * @returns {Map<string, ServiceAnnotationConfig>} - Service Annotation Config map. order starts from 1.
 */
export const createServiceAnnoOrderMapByCode = (
  annoByServiceCodes: AnnoByServiceCode[]
): Map<string, ServiceAnnotationConfig> => {
  const map = new Map<string, ServiceAnnotationConfig>();
  annoByServiceCodes.forEach((anno, index) => {
    map.set(anno.code, { order: index + 1, annotation: anno.annotation });
  });
  return map;
};

/**
 * Create a map of code, annotation and order from publishing plans. Each plan contains services.
 * @param {Plan[]} plans - Plan array
 * @returns {Map<string, ServiceAnnotationConfig>} - Service Annotation Config map. order starts from 1.
 */
export const createServiceAnnoOrderMapByCodeFromPlans = (
  plans?: Plan[]
): Map<string, ServiceAnnotationConfig> => {
  // if plans is falsy or empty, return an empty map.
  if (!plans || plans.length === 0) {
    return new Map<string, ServiceAnnotationConfig>();
  }

  // extract plans those are published currently ( that means planStatus === 'active' )
  const plansPublishingCurrently = plans.filter(
    (p) => p.status === PlanStatus.Active
  );

  // create services set from all plans
  const serviceCodeSet = new Set<string>();
  plansPublishingCurrently.forEach((p) => {
    p?.services?.forEach((s) => {
      if (s) {
        serviceCodeSet.add(s.code);
      }
    });
  });
  const serviceCodes = Array.from(serviceCodeSet);

  // ---------------------------------
  // create annotationCodesMap
  // ---------------------------------
  let annoByServiceCodes: AnnoByServiceCode[] = [];
  // 1st: MfcInvoice
  if (serviceCodes.includes(ServiceNames.MfcInvoice.CODE)) {
    annoByServiceCodes.push({
      code: ServiceNames.MfcInvoice.CODE,
      annotation: ServiceNames.MfcInvoice.Annotation,
    });
  }
  // 2nd: ExternalBenefitSelect
  if (serviceCodes.includes(ServiceNames.ExternalBenefitSelect.CODE)) {
    annoByServiceCodes.push({
      code: ServiceNames.ExternalBenefitSelect.CODE,
      annotation: ServiceNames.ExternalBenefitSelect.Annotation,
    });
  }

  // create annotationOrderMap
  const serviceAnnotationOrderMap =
    createServiceAnnoOrderMapByCode(annoByServiceCodes);
  return serviceAnnotationOrderMap;
};

export const calcAvailableServices = (availableServices?: Service[]) => {
  if (!availableServices) {
    return {
      mikatanoServices: [],
      externalServices: [],
      loanPortalService: undefined,
    };
  }
  const mikatanoServices = availableServices.filter(
    (svc) =>
      svc.type === ServiceType.Mikatano && svc.code !== ServiceNames.Loan.CODE
  );

  const externalServices = availableServices.filter(
    (svc) => svc.type === ServiceType.Other
  );

  const loanPortalService = availableServices.find(
    (svc) => svc.code === ServiceNames.Loan.CODE
  );

  return { mikatanoServices, externalServices, loanPortalService };
};
